import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Trophy,
  XCircle,
  CheckCircle,
  Clock,
  BarChart3,
  FileText,
  ArrowLeft,
  AlertTriangle,
  Sparkles,
  Loader2,
  Bot,
  AlertCircle,
  Save,
  MessageSquare,
  PenLine,
  ChevronDown,
  ChevronUp,
  User,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExamResultsPage() {
  const { workspaceId, attemptId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [aiGrading, setAiGrading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Per-question grading state: { [questionId]: { points, comment } }
  const [grades, setGrades] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    if (attemptId) {
      loadResult();
    }
  }, [attemptId]);
  
  const loadResult = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attemptId}/result`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Impossible de charger les résultats');
      }
      
      const data = await response.json();
      setResult(data);
      
      // Initialize grades from existing data
      const initialGrades = {};
      let hasUnsavedAiGrades = false;
      
      (data.questions || []).forEach((q) => {
        let points = q.points_earned ?? 0;
        
        // Pré-remplir avec la note de l'IA si elle existe et que ce n'est pas encore corrigé manuellement
        if (q.ai_grading && q.ai_grading.score !== undefined && !q.manually_graded) {
          points = q.ai_grading.score;
          hasUnsavedAiGrades = true;
        }
        
        initialGrades[q.id] = {
          points: points,
          comment: q.manual_comment || ''
        };
      });
      setGrades(initialGrades);
      setHasChanges(hasUnsavedAiGrades);
      
      // Expand all questions by default
      setExpandedQuestions(new Set((data.questions || []).map(q => q.id)));
    } catch (error) {
      console.error('Failed to load result:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const updateGrade = (questionId, field, value) => {
    setGrades(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
    setHasChanges(true);
  };
  
  const handleSaveGrades = async () => {
    setSaving(true);
    try {
      const gradesList = Object.entries(grades).map(([questionId, grade]) => ({
        question_id: questionId,
        points_earned: parseFloat(grade.points) || 0,
        comment: grade.comment || ''
      }));
      
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attemptId}/manual-grade`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            grades: gradesList,
            grader_id: user?.id
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la sauvegarde');
      }
      
      const data = await response.json();
      toast.success(`Notes enregistrées — Score: ${data.percentage.toFixed(1)}% ${data.passed ? '✓ Réussi' : '✗ Non réussi'}`);
      setHasChanges(false);
      
      // Reload to update stats
      await loadResult();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleAIGrading = async () => {
    if (!workspaceId) {
      toast.error('Workspace non trouvé');
      return;
    }
    
    setAiGrading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attemptId}/ai-grade?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la correction IA');
      }
      
      const data = await response.json();
      
      if (data.questions_graded > 0) {
        toast.success(`${data.questions_graded} question(s) corrigée(s) par l'IA`);
        // Reload to get AI scores then auto-save
        await loadResult();
        // Auto-save the AI grades as final grades
        setTimeout(async () => {
          try {
            const currentGrades = {};
            const freshRes = await fetch(
              `${API_URL}/api/exams/attempts/${attemptId}/result`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (freshRes.ok) {
              const freshData = await freshRes.json();
              (freshData.questions || []).forEach((q) => {
                let points = q.points_earned ?? 0;
                if (q.ai_grading && q.ai_grading.score !== undefined) {
                  points = q.ai_grading.score;
                }
                currentGrades[q.id] = { points, comment: q.manual_comment || '' };
              });
              
              const gradesList = Object.entries(currentGrades).map(([questionId, grade]) => ({
                question_id: questionId,
                points_earned: parseFloat(grade.points) || 0,
                comment: grade.comment || ''
              }));
              
              const saveRes = await fetch(
                `${API_URL}/api/exams/attempts/${attemptId}/manual-grade`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ grades: gradesList, grader_id: user?.id })
                }
              );
              
              if (saveRes.ok) {
                toast.success('Notes enregistrées automatiquement');
                setHasChanges(false);
                await loadResult();
              }
            }
          } catch (e) {
            console.error('Auto-save after AI grading failed:', e);
          }
        }, 500);
      } else if (data.questions_already_graded > 0) {
        toast.info('Toutes les questions ont déjà été corrigées');
      } else {
        toast.info("Aucune question à corriger par l'IA");
      }
    } catch (error) {
      console.error('AI grading error:', error);
      toast.error(error.message);
    } finally {
      setAiGrading(false);
    }
  };

  const toggleQuestion = (qid) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(qid)) {
        newSet.delete(qid);
      } else {
        newSet.add(qid);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedQuestions(new Set((result?.questions || []).map(q => q.id)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      'single_choice': 'QCM unique',
      'multiple_choice': 'QCM multiple',
      'true_false': 'Vrai/Faux',
      'short_answer': 'Réponse courte',
      'long_answer': 'Réponse longue',
      'essay': 'Dissertation'
    };
    return labels[type] || type;
  };

  // Calculate live totals from current grades
  const calcLiveTotals = () => {
    if (!result?.questions) return { earned: 0, total: 0, pct: 0 };
    let earned = 0;
    let total = 0;
    result.questions.forEach(q => {
      total += q.points || 1;
      earned += parseFloat(grades[q.id]?.points ?? q.points_earned) || 0;
    });
    const pct = total > 0 ? (earned / total * 100) : 0;
    return { earned, total, pct };
  };
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des résultats...</p>
          </div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  if (error || !result) {
    return (
      <WorkspaceLayout>
        <div className="p-6 text-center max-w-md mx-auto">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Résultat non trouvé</h1>
          <p className="text-muted-foreground mb-6">{error || 'Impossible de charger les résultats de cet examen.'}</p>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/exams/${result?.exam_id || ''}/attempts`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux tentatives
          </Button>
        </div>
      </WorkspaceLayout>
    );
  }
  
  const passed = result.passed;
  const percentage = result.percentage || 0;
  const passingScore = result.passing_score || 50;
  const hasPendingAIGrading = result.has_pending_ai_grading;
  const liveTotals = calcLiveTotals();
  
  return (
    <WorkspaceLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="exam-results-page">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate(`/workspace/${workspaceId}/exams/${result.exam_id}/attempts`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux tentatives
        </Button>
        
        {/* Result Header */}
        <Card className={cn(
          "overflow-hidden border-2",
          passed 
            ? "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200"
            : "bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950 border-red-200"
        )}>
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className={cn(
                "w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center",
                passed ? "bg-green-100 dark:bg-green-900" : "bg-red-100 dark:bg-red-900"
              )}>
                {passed ? (
                  <Trophy className="h-8 w-8 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                )}
              </div>
              
              <Badge className={cn("text-lg px-4 py-1 mb-4", passed ? "bg-green-500" : "bg-red-500")}>
                {passed ? 'Examen réussi !' : 'Examen non réussi'}
              </Badge>
              
              <div className="mb-2">
                <span className="text-5xl font-bold">{percentage.toFixed(1)}%</span>
              </div>
              
              <p className="text-muted-foreground text-sm">
                Score minimum requis : {passingScore}%
              </p>
              
              <h2 className="text-xl font-semibold mt-4">{result.exam_title}</h2>
              
              {result.external_candidate && (
                <div className="flex items-center justify-center gap-2 mt-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  Candidat : {result.user_name}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Score Details */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Score obtenu</p>
                  <p className="text-2xl font-bold">
                    {result.total_score?.toFixed(1) || 0} / {result.max_score || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Questions</p>
                  <p className="text-2xl font-bold">
                    {result.questions?.filter((q) => q.is_correct).length || 0} / {result.questions?.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Soumis le</p>
                  <p className="text-lg font-medium">
                    {result.submitted_at ? formatDate(result.submitted_at) : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Bar: Save + AI Grade — only for trainers/admins */}
        {canManage && (
        <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/30">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <PenLine className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="font-medium text-blue-800 dark:text-blue-200">Correction manuelle</h3>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Notez chaque question individuellement ci-dessous
                    {hasChanges && <span className="ml-2 text-amber-600 font-medium">• Modifications non sauvegardées</span>}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {hasPendingAIGrading && user?.role === 'admin' && (
                  <Button 
                    onClick={handleAIGrading}
                    disabled={aiGrading}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {aiGrading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Correction IA...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" />Corriger par IA</>
                    )}
                  </Button>
                )}
                <Button 
                  onClick={handleSaveGrades}
                  disabled={saving || !hasChanges}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Enregistrer les notes</>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Live score preview */}
            {hasChanges && (
              <div className="mt-3 pt-3 border-t border-blue-200 flex items-center gap-4 text-sm">
                <span className="text-blue-700 font-medium">Aperçu :</span>
                <span className="font-bold text-lg">{liveTotals.pct.toFixed(1)}%</span>
                <span className="text-muted-foreground">
                  ({liveTotals.earned.toFixed(1)} / {liveTotals.total} pts)
                </span>
                <Badge className={liveTotals.pct >= passingScore ? "bg-green-500" : "bg-red-500"}>
                  {liveTotals.pct >= passingScore ? 'Réussi' : 'Non réussi'}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
        )}
        
        {/* Violations warning */}
        {result.violations_count > 0 && (
          <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-orange-600 shrink-0" />
                <div>
                  <h3 className="font-medium text-orange-800 dark:text-orange-200">
                    Avertissements de surveillance
                  </h3>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    {result.violations_count} infraction(s) détectée(s) pendant l'examen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Questions Grading Section */}
        {result.questions && result.questions.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Correction des réponses
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Attribuez une note et un commentaire à chaque question
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={expandAll}>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Tout ouvrir
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll}>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Tout fermer
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.questions.map((q, index) => {
                const isExpanded = expandedQuestions.has(q.id);
                const currentPoints = parseFloat(grades[q.id]?.points ?? q.points_earned) || 0;
                const maxPoints = q.points || 1;
                const isFullMark = currentPoints >= maxPoints;
                const isZero = currentPoints === 0;
                
                return (
                  <div
                    key={q.id}
                    className={cn(
                      "border rounded-xl overflow-hidden transition-all",
                      q.manually_graded 
                        ? "border-blue-200 bg-blue-50/30"
                        : isFullMark 
                          ? "border-green-200 bg-green-50/30"
                          : isZero
                            ? "border-red-200 bg-red-50/30"
                            : "border-orange-200 bg-orange-50/30"
                    )}
                  >
                    {/* Question Header (clickable) */}
                    <button
                      className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-black/5 transition-colors"
                      onClick={() => toggleQuestion(q.id)}
                    >
                      {/* Status icon */}
                      <span className={cn(
                        "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
                        isFullMark 
                          ? "bg-green-100 text-green-700"
                          : isZero 
                            ? "bg-red-100 text-red-700"
                            : "bg-orange-100 text-orange-700"
                      )}>
                        {isFullMark ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : isZero ? (
                          <XCircle className="h-5 w-5" />
                        ) : (
                          <span>{currentPoints.toFixed(1)}</span>
                        )}
                      </span>
                      
                      {/* Question info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">
                          Question {index + 1}
                          <span className="ml-2 text-xs text-muted-foreground font-normal">
                            ({getQuestionTypeLabel(q.type)})
                          </span>
                          {q.manually_graded && (
                            <Badge variant="outline" className="ml-2 text-xs bg-blue-100 text-blue-700 border-blue-200">
                              <PenLine className="h-3 w-3 mr-1" />
                              Corrigé
                            </Badge>
                          )}
                          {q.ai_grading && (
                            <Badge variant="outline" className="ml-2 text-xs bg-purple-100 text-purple-700 border-purple-200">
                              <Bot className="h-3 w-3 mr-1" />
                              IA
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {q.question_text?.replace(/<[^>]*>/g, '').substring(0, 80)}{q.question_text?.replace(/<[^>]*>/g, '').length > 80 ? '...' : ''}
                        </p>
                      </div>
                      
                      {/* Score badge */}
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge variant="outline" className={cn(
                          "text-sm font-mono",
                          isFullMark ? "bg-green-100 text-green-700 border-green-300" :
                          isZero ? "bg-red-100 text-red-700 border-red-300" :
                          "bg-orange-100 text-orange-700 border-orange-300"
                        )}>
                          {currentPoints.toFixed(1)} / {maxPoints} pts
                        </Badge>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    
                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-5 pb-5 space-y-4 border-t">
                        {/* Full question text */}
                        <div className="pt-4">
                          <p className="font-medium text-sm text-muted-foreground mb-2">Énoncé :</p>
                          <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 text-sm prose prose-sm max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: q.question_text }} />
                          </div>
                        </div>
                        
                        {/* Choices (for QCM) */}
                        {q.choices && q.choices.length > 0 && (
                          <div>
                            <p className="font-medium text-sm text-muted-foreground mb-2">Choix :</p>
                            <div className="space-y-1.5">
                              {q.choices.map((choice) => {
                                const isUserAnswer = Array.isArray(q.user_answer) 
                                  ? q.user_answer.includes(choice.id)
                                  : q.user_answer === choice.id;
                                const isCorrect = Array.isArray(q.correct_answer)
                                  ? q.correct_answer.includes(choice.id)
                                  : q.correct_answer === choice.id;
                                
                                return (
                                  <div
                                    key={choice.id}
                                    className={cn(
                                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm border",
                                      isCorrect && isUserAnswer 
                                        ? "bg-green-50 border-green-300 text-green-800"
                                        : isCorrect
                                          ? "bg-green-50 border-green-200 text-green-700"
                                          : isUserAnswer
                                            ? "bg-red-50 border-red-300 text-red-800"
                                            : "bg-white border-slate-200 text-slate-600"
                                    )}
                                  >
                                    {isCorrect ? (
                                      <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                                    ) : isUserAnswer ? (
                                      <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                                    ) : (
                                      <div className="h-4 w-4 rounded-full border-2 border-slate-300 shrink-0" />
                                    )}
                                    <span>{choice.text}</span>
                                    {isUserAnswer && (
                                      <Badge variant="outline" className="ml-auto text-xs">
                                        Réponse candidat
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Text answer (for short/long answer) */}
                        {q.type && !['single_choice', 'multiple_choice', 'true_false'].includes(q.type) && (
                          <div>
                            <p className="font-medium text-sm text-muted-foreground mb-2">Réponse du candidat :</p>
                            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 text-sm whitespace-pre-wrap">
                              {q.user_answer_display || q.user_answer || (
                                <span className="text-muted-foreground italic">Pas de réponse</span>
                              )}
                            </div>
                            {q.correct_answer_display && (
                              <div className="mt-2">
                                <p className="font-medium text-sm text-muted-foreground mb-1">Réponse attendue :</p>
                                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
                                  {q.correct_answer_display}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* AI Grading Result */}
                        {q.ai_grading && (
                          <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950 border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Bot className="h-4 w-4 text-purple-600" />
                              <span className="font-medium text-purple-800 text-sm">Correction IA</span>
                              <Badge variant="outline" className="ml-auto text-xs">
                                Confiance: {(q.ai_grading.confidence * 100).toFixed(0)}%
                              </Badge>
                            </div>
                            <p className="text-sm text-purple-700">
                              <strong>Score IA:</strong> {q.ai_grading.score} / {q.ai_grading.max_score} pts
                            </p>
                            <p className="text-sm text-purple-700 mt-1">
                              <strong>Feedback:</strong> {q.ai_grading.feedback}
                            </p>
                          </div>
                        )}

                        <Separator />
                        
                        {/* ===== GRADING SECTION — only for text questions ===== */}
                        {['short_answer', 'long_answer', 'essay'].includes(q.type) && canManage ? (
                          <div className="bg-white dark:bg-slate-950 rounded-xl border-2 border-blue-200 p-4 space-y-4">
                            <div className="flex items-center gap-2">
                              <PenLine className="h-4 w-4 text-blue-600" />
                              <span className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                                Notation manuelle
                              </span>
                            </div>
                            
                            {/* Points input */}
                            <div className="flex items-center gap-3">
                              <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                                Note :
                              </label>
                              <Input
                                type="number"
                                min="0"
                                max={maxPoints}
                                step="0.5"
                                value={grades[q.id]?.points ?? ''}
                                onChange={(e) => updateGrade(q.id, 'points', e.target.value)}
                                className="w-24 text-center font-bold text-lg"
                              />
                              <span className="text-sm font-medium text-muted-foreground">
                                sur {maxPoints.toFixed(2)}
                              </span>
                              
                              {/* Quick buttons */}
                              <div className="flex gap-1 ml-auto">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 px-2 text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => updateGrade(q.id, 'points', 0)}
                                >
                                  0
                                </Button>
                                {maxPoints > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7 px-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                                    onClick={() => updateGrade(q.id, 'points', (maxPoints / 2).toFixed(1))}
                                  >
                                    {(maxPoints / 2).toFixed(1)}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 px-2 text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => updateGrade(q.id, 'points', maxPoints)}
                                >
                                  {maxPoints}
                                </Button>
                              </div>
                            </div>
                            
                            {/* Comment */}
                            <div>
                              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-2">
                                <MessageSquare className="h-3.5 w-3.5" />
                                Commentaire (optionnel) :
                              </label>
                              <Textarea
                                placeholder="Ajoutez un commentaire pour le candidat..."
                                value={grades[q.id]?.comment ?? ''}
                                onChange={(e) => updateGrade(q.id, 'comment', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </div>
                        ) : ['short_answer', 'long_answer', 'essay'].includes(q.type) ? (
                          /* Text question: read-only grade display for candidates */
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                            {currentPoints > 0 ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                            <span className="text-sm font-medium">
                              {q.manually_graded || q.ai_grading ? 'Corrigé' : 'En attente de correction'}
                            </span>
                            <Badge variant="outline" className="ml-auto">
                              {currentPoints.toFixed(1)} / {maxPoints} pts
                            </Badge>
                          </div>
                        ) : (
                          /* QCM / Vrai-Faux: auto-corrected, read-only result */
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                            {isFullMark ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                            <span className="text-sm font-medium">
                              {isFullMark ? 'Correction automatique : Correct' : 'Correction automatique : Incorrect'}
                            </span>
                            <Badge variant="outline" className="ml-auto">
                              {currentPoints.toFixed(1)} / {maxPoints} pts
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
        
        {/* Bottom Save Bar — only for trainers/admins */}
        {canManage && hasChanges && (
          <div className="sticky bottom-4 z-10">
            <Card className="border-2 border-blue-300 bg-blue-50 shadow-lg">
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    Modifications non sauvegardées
                  </span>
                  <span className="text-sm text-blue-600">
                    — Score prévu : {liveTotals.pct.toFixed(1)}% ({liveTotals.earned.toFixed(1)}/{liveTotals.total} pts)
                  </span>
                </div>
                <Button 
                  onClick={handleSaveGrades}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Enregistrer les notes</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex justify-center gap-4 pb-8">
          <Button variant="outline" onClick={() => navigate(
            canManage 
              ? `/workspace/${workspaceId}/exams/${result.exam_id}/attempts`
              : `/workspace/${workspaceId}/exams`
          )}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {canManage ? 'Retour aux tentatives' : 'Retour aux examens'}
          </Button>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
